import type { WorkerInstance } from "./createLookupWorker";
import { devLogger } from "@certa/common/utils/devLogger";

/**
 * Debounced worker message queue to prevent memory issues from excessive worker calls
 *
 * Problems this solves:
 * 1. Excessive structured cloning of large megaJSON on every change
 * 2. Worker processing backlog when rapid changes occur
 * 3. Memory pressure from multiple pending computations
 */

type WorkerMessage<T = any> = {
  data: T;
  timestamp: number;
};

const DEBOUNCE_DELAY = 500; // 500ms - balance between responsiveness and performance
const MAX_PENDING_MESSAGES = 2; // Only keep latest message if worker is busy

class WorkerMessageQueue {
  private pendingTimeouts: Map<Worker, NodeJS.Timeout> = new Map();
  private queuedMessages: Map<Worker, WorkerMessage> = new Map();

  /**
   * Post a message to the worker with debouncing and queue management
   * - Cancels previous pending messages
   * - Debounces rapid calls
   * - Drops intermediate messages if worker is busy
   */
  postDebounced<T>(worker: WorkerInstance | null, message: T): void {
    if (!worker) {
      devLogger.warn("Worker message queue: worker is null");
      return;
    }

    // Cancel previous debounced call
    const existingTimeout = this.pendingTimeouts.get(worker);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Check if worker is busy processing
    const pendingCount = worker.__pendingMessageCount || 0;
    const isProcessing = worker.__isProcessing || false;

    if (isProcessing || pendingCount >= MAX_PENDING_MESSAGES) {
      // Worker is busy - queue this message and drop older queued messages
      devLogger.info(
        `Worker busy (pending: ${pendingCount}), queuing message and dropping older ones`
      );
      this.queuedMessages.set(worker, {
        data: message,
        timestamp: Date.now()
      });
      return;
    }

    // Queue the message with debouncing
    const timeout = setTimeout(() => {
      this.sendMessage(worker, message);
      this.pendingTimeouts.delete(worker);
    }, DEBOUNCE_DELAY);

    this.pendingTimeouts.set(worker, timeout);
  }

  /**
   * Actually send the message to the worker
   */
  private sendMessage<T>(worker: WorkerInstance, message: T): void {
    try {
      worker.__isProcessing = true;
      worker.__pendingMessageCount = (worker.__pendingMessageCount || 0) + 1;

      worker.postMessage(message);

      devLogger.info(
        `Sent message to worker (pending: ${worker.__pendingMessageCount})`
      );
    } catch (error) {
      devLogger.error("Failed to send message to worker", error);
      worker.__isProcessing = false;
      worker.__pendingMessageCount = Math.max(
        0,
        (worker.__pendingMessageCount || 0) - 1
      );
    }
  }

  /**
   * Process any queued messages for this worker
   * Call this when worker finishes processing
   */
  processQueue(worker: WorkerInstance): void {
    const queuedMessage = this.queuedMessages.get(worker);
    if (queuedMessage) {
      this.queuedMessages.delete(worker);

      // Send the queued message after a short delay
      setTimeout(() => {
        this.postDebounced(worker, queuedMessage.data);
      }, 100);
    }
  }

  /**
   * Clear all pending messages for a worker
   * Call this when worker is terminated
   */
  clearWorker(worker: Worker): void {
    const timeout = this.pendingTimeouts.get(worker);
    if (timeout) {
      clearTimeout(timeout);
      this.pendingTimeouts.delete(worker);
    }
    this.queuedMessages.delete(worker);
  }

  /* For debugging */
  // getStats(worker: Worker): {
  //   hasPendingTimeout: boolean;
  //   hasQueuedMessage: boolean;
  //   queuedMessageAge?: number;
  // } {
  //   const queuedMessage = this.queuedMessages.get(worker);
  //   return {
  //     hasPendingTimeout: this.pendingTimeouts.has(worker),
  //     hasQueuedMessage: !!queuedMessage,
  //     queuedMessageAge: queuedMessage
  //       ? Date.now() - queuedMessage.timestamp
  //       : undefined
  //   };
  // }
}

// Singleton instance
export const workerMessageQueue = new WorkerMessageQueue();
